import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { isMotionPaused } from '@/lib/motion'
import { PrimaryButton } from '@/components/generals/primary-button'

import { PROJECT_TYPES } from '@/constants/studio'

type FieldName = 'name' | 'email' | 'type' | 'scope'

type Status = 'idle' | 'sending' | 'collapsing' | 'sent' | 'error'

type Values = Record<FieldName, string>

type Errors = Partial<Record<FieldName, string>>

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const SCOPE_MAX = 4000

const validate = (values: Values): Errors => {
    const errors: Errors = {}

    if (values.name.trim().length < 2) errors.name = 'Please enter your name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (!PROJECT_TYPES.includes(values.type)) errors.type = 'Please choose a project type.'
    if (values.scope.trim().length < 20) errors.scope = 'Please describe the project in at least 20 characters.'

    return errors
}

const inputClass = 'mt-2 block w-full border-0 border-b border-hairline bg-transparent px-0 py-3 font-mono text-[15px] tracking-[0.02em] text-ink transition-colors duration-300 placeholder:text-dim hover:border-mute focus:border-signal'

export const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<Values>({ name: '', email: '', type: '', scope: '' })
    const [errors, setErrors] = useState<Errors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const honeypotRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const receivedRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (status !== 'collapsing') return

        const timer = window.setTimeout(() => setStatus('sent'), 820)

        return () => window.clearTimeout(timer)
    }, [status])

    useEffect(() => {
        if (status === 'sent') receivedRef.current?.focus()
    }, [status])

    const update = (field: FieldName) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        const value = field === 'scope' ? event.target.value.slice(0, SCOPE_MAX) : event.target.value

        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') return

        const found = validate(values)

        setErrors(found)

        const first = (['name', 'email', 'type', 'scope'] as Array<FieldName>).find(field => found[field])

        if (first) {
            formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus()

            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, website: honeypotRef.current?.value ?? '' })
            })
            const payload = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: Errors } | null

            if (response.ok && payload?.ok) {
                const instant = isMotionPaused() || window.matchMedia('(prefers-reduced-motion: reduce)').matches

                setStatus(instant ? 'sent' : 'collapsing')

                return
            }

            if (payload?.errors) setErrors(payload.errors)

            setStatus('error')
            setMessage(payload?.message ?? 'Something went wrong while sending. Please try again.')
        }
        catch {
            setStatus('error')
            setMessage('The connection dropped before your brief was sent. Please try again.')
        }
    }

    if (status === 'sent') {
        return (
            <div ref={receivedRef} tabIndex={-1} role='status' className='mt-12 outline-none'>
                <span className='block h-px w-full origin-left bg-ink animate-in fade-in duration-500' aria-hidden='true' />

                <p className='mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-2 font-mono text-[clamp(18px,2.2vw,26px)] uppercase tracking-[0.12em] animate-in fade-in slide-in-from-bottom-2 duration-700'>
                    <span className='flex items-center gap-3 text-ink'>
                        <span aria-hidden='true' className='inline-block size-2 bg-signal' />
                        Received
                    </span>

                    <span className='text-dim' aria-hidden='true'>
                        /
                    </span>

                    <span className='text-ink'>
                        We reply within two days
                    </span>
                </p>

                <p className='body-copy mt-6 text-ink/75 animate-in fade-in duration-700'>
                    A partner in Stockholm or Lisbon has your brief. Their first reply usually asks about the site and any drawings or surveys that already exist.
                </p>

                <button
                    type='button'
                    onClick={() => {
                        setValues({ name: '', email: '', type: '', scope: '' })
                        setStatus('idle')
                    }}
                    className='hud mt-8 inline-flex items-center border-b border-ink/45 pb-1 text-ink transition-colors duration-300 hover:border-ink'
                >
                    Send another brief
                </button>
            </div>
        )
    }

    const collapsing = status === 'collapsing'

    const line = cn(
        'grid transition-[grid-template-rows,opacity] duration-500 ease-instrument',
        collapsing ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr]'
    )

    const inner = cn('min-h-0 pb-9', collapsing && 'overflow-hidden')

    const lineDelay = (index: number): { transitionDelay: string } => ({ transitionDelay: collapsing ? `${index * 80}ms` : '0ms' })

    const field = (name: FieldName): { invalid: boolean, describedBy: string | undefined } => ({
        invalid: Boolean(errors[name]),
        describedBy: errors[name] ? `contact-${name}-error` : undefined
    })

    return (
        <form ref={formRef} onSubmit={submit} noValidate className='mt-12' aria-describedby={message ? 'contact-status' : undefined} aria-busy={collapsing || undefined}>
            <div className={line} style={lineDelay(0)}>
                <div className={inner}>
                    <label htmlFor='contact-name' className='hud text-mute'>
                        Name
                    </label>

                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={field('name').invalid}
                        aria-describedby={field('name').describedBy}
                        placeholder='Your full name'
                        className={cn(inputClass, errors.name && 'border-signal')}
                    />

                    {errors.name && (
                        <p id='contact-name-error' className='hud mt-2 text-signal'>
                            {errors.name}
                        </p>
                    )}
                </div>
            </div>

            <div className={line} style={lineDelay(1)}>
                <div className={inner}>
                    <label htmlFor='contact-email' className='hud text-mute'>
                        Email
                    </label>

                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={field('email').invalid}
                        aria-describedby={field('email').describedBy}
                        placeholder='name@practice.com'
                        className={cn(inputClass, errors.email && 'border-signal')}
                    />

                    {errors.email && (
                        <p id='contact-email-error' className='hud mt-2 text-signal'>
                            {errors.email}
                        </p>
                    )}
                </div>
            </div>

            <div className={line} style={lineDelay(2)}>
                <fieldset className={inner} aria-describedby={field('type').describedBy}>
                    <legend className='hud text-mute'>
                        Project type
                    </legend>

                    <div className='mt-3 flex flex-wrap gap-2'>
                        {PROJECT_TYPES.map((type, index) => (
                            <label key={type} className='relative cursor-pointer'>
                                <input
                                    type='radio'
                                    name='type'
                                    value={type}
                                    checked={values.type === type}
                                    onChange={() => {
                                        setValues(current => ({ ...current, type }))

                                        if (errors.type) setErrors(current => ({ ...current, type: undefined }))
                                    }}
                                    aria-invalid={index === 0 ? field('type').invalid : undefined}
                                    className='peer sr-only'
                                />

                                <span className={cn(
                                    'hud flex h-10 items-center border px-3.5 transition-colors duration-300 peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-signal',
                                    values.type === type ? 'border-ink bg-ink text-background' : 'border-hairline text-mute hover:border-mute hover:text-ink',
                                    errors.type && values.type !== type && 'border-signal/60'
                                )}>
                                    {type}
                                </span>
                            </label>
                        ))}
                    </div>

                    {errors.type && (
                        <p id='contact-type-error' className='hud mt-2 text-signal'>
                            {errors.type}
                        </p>
                    )}
                </fieldset>
            </div>

            <div className={line} style={lineDelay(3)}>
                <div className={inner}>
                    <div className='flex items-baseline justify-between gap-4'>
                        <label htmlFor='contact-scope' className='hud text-mute'>
                            Project scope
                        </label>

                        <span className='hud text-mute' aria-hidden='true'>
                            {String(values.scope.length).padStart(4, '0')} / {SCOPE_MAX}
                        </span>
                </div>

                <textarea
                    id='contact-scope'
                    name='scope'
                    rows={5}
                    value={values.scope}
                    onChange={update('scope')}
                    aria-invalid={field('scope').invalid}
                    aria-describedby={field('scope').describedBy}
                    placeholder='The building, where it is, what stage it is at and what you need measured, designed or monitored.'
                    className={cn(inputClass, 'min-h-[140px] resize-y leading-[1.6]', errors.scope && 'border-signal')}
                />

                {errors.scope && (
                    <p id='contact-scope-error' className='hud mt-2 text-signal'>
                        {errors.scope}
                    </p>
                )}
                </div>
            </div>

            <div className='absolute -left-[9999px] h-px w-px overflow-hidden' aria-hidden='true'>
                <label htmlFor='contact-website'>
                    Website
                </label>

                <input id='contact-website' ref={honeypotRef} name='website' type='text' tabIndex={-1} autoComplete='off' />
            </div>

            <div className={cn('flex flex-wrap items-center gap-x-8 gap-y-4 pt-2 transition-opacity duration-300', collapsing && 'opacity-0')} style={lineDelay(4)}>
                <PrimaryButton type='submit' busy={status === 'sending' || collapsing}>
                    {status === 'sending' || collapsing ? 'Transmitting' : 'Engage the studio'}
                </PrimaryButton>

                <p className='hud text-mute'>
                    Stored securely / Reply within 2 working days
                </p>
            </div>

            {message && (
                <p id='contact-status' role='alert' className='hud border border-signal/60 px-4 py-3 text-ink'>
                    {message}
                </p>
            )}
        </form>
    )
}
